import React, { useState, useRef, useEffect } from 'react';
import { InputCommon } from 'components/common';
import { ProductVersion } from 'models';
import { ProductSelectItem } from 'components/Product';
import { getFilePath } from 'utils';
import { SpinnerGap } from '@phosphor-icons/react';

interface ProductSearchInputProps {
  placeholder?: string;
  options: ProductVersion[];
  onSelect: (selectedOption: { value: string; label: React.ReactNode }) => void;
  onSearch: (searchTerm: string) => void;
  loading?: boolean;
  value?: Array<{ value: string; label: string }>;
  suffixIcon?: React.ReactNode;
  notFoundContent?: React.ReactNode;
  className?: string;
}

const ProductSearchInput: React.FC<ProductSearchInputProps> = ({
  placeholder = 'Tìm kiếm sản phẩm...',
  options = [],
  onSelect,
  onSearch,
  loading = false,
  // value = [],
  suffixIcon,
  notFoundContent,
  className = '',
}) => {
  const [searchText, setSearchText] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        inputRef.current &&
        dropdownRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchText = e.target.value;
    setSearchText(newSearchText);
    onSearch(newSearchText);
    setIsDropdownOpen(true);
  };

  const handleSelectItem = (product: ProductVersion) => {
    const selectedOption = {
      value: product.id || '',
      label: product.name || '',
    };
    onSelect(selectedOption);
    setIsDropdownOpen(false);
  };

  const handleInputFocus = () => {
    setIsDropdownOpen(true);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className='relative' ref={inputRef}>
      <div className='relative'>
        <InputCommon
          placeholder={placeholder}
          value={searchText}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          variant='borderless'
          className={`${className} !bg-[var(--textBlack4)] `}
          radius='rounded'
          addonAfterNode={suffixIcon}
        />
      </div>

      <div
        ref={dropdownRef}
        className={`absolute p-1 z-50 w-full mt-1 bg-white shadow-lg rounded-md max-h-[300px] overflow-y-auto ${
          isDropdownOpen ? 'block' : 'hidden'
        }`}
        style={{ minWidth: '300px' }}
      >
        {loading ? (
          <div className='flex items-center justify-center p-4'>
            <SpinnerGap size={18} weight='bold' className='text-[var(--primaryColor500)] animate-spin' />
            <span className='ml-2'>Đang tìm kiếm...</span>
          </div>
        ) : options.length === 0 ? (
          <div className='p-4 text-center text-gray-500'>{notFoundContent || 'Không tìm thấy sản phẩm'}</div>
        ) : (
          <div className='py-1 flex flex-col gap-2'>
            {options.map((product) => (
              <div
                key={product.id}
                className='cursor-pointer hover:bg-gray-100 transition-colors px-4 py-2 rounded-md'
                onClick={() => handleSelectItem(product)}
              >
                <ProductSelectItem
                  imageUrl={
                    product.productImages?.[0]?.image?.filePath
                      ? getFilePath() + '/' + product.productImages[0].image.filePath
                      : undefined
                  }
                  name={
                    product.name +
                    (product.color ? ` - ${product.color}` : '') +
                    (product.dimension ? ` - ${product.dimension}` : '') +
                    (product.sizeSpec ? ` - ${product.sizeSpec}` : '')
                  }
                  price={product.salePrice}
                  quantity={product.product?.quantity}
                  brand={product.brand?.name || '---'}
                  category={product.category?.name || '---'}
                  code={product.product?.code}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductSearchInput;
